import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Theme = 'light' | 'dark';
const STORAGE_KEY = 'sx.theme';

interface ThemeValue {
  theme: Theme;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

function initialTheme(): Theme {
  try {
    // ?theme=dark forces a theme for this load. It exists so a screenshot or
    // a review link can pin one without first clicking the toggle, which a
    // headless browser cannot do. It does not persist: the stored preference
    // and the OS setting still govern every other visit.
    const requested = new URLSearchParams(window.location.search).get('theme');
    if (requested === 'light' || requested === 'dark') return requested;

    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    // Private mode or blocked storage — the OS preference is unavailable too.
    return 'light';
  }
}

/** True when the theme came from the URL, so it should not be written back. */
function isPinned(): boolean {
  try {
    const requested = new URLSearchParams(window.location.search).get('theme');
    return requested === 'light' || requested === 'dark';
  } catch {
    return false;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    // The attribute is what the token file keys off; nothing else switches.
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme;
    // A URL-pinned theme is for this load only; writing it back would make a
    // screenshot link silently change the reviewer's own preference.
    if (isPinned()) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* the choice simply will not survive a reload */
    }
  }, [theme]);

  const toggle = useCallback(() => setTheme((current) => (current === 'dark' ? 'light' : 'dark')), []);
  const value = useMemo(() => ({ theme, toggle }), [theme, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside ThemeProvider');
  return context;
}

/** The switch itself, so the control looks identical everywhere it appears. */
export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const dark = theme === 'dark';

  return (
    <button
      className="themebtn"
      onClick={toggle}
      aria-pressed={dark}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Light mode' : 'Dark mode'}
    >
      <span aria-hidden="true">{dark ? '☀' : '◐'}</span>
    </button>
  );
}
